"use client"

/**
 * DropdownMenu - the platform's one menu for "more actions" style lists.
 *
 * A thin, styled wrapper over the Base UI Menu primitive (keyboard support,
 * focus management and ARIA roles come from the primitive). Use it wherever a
 * screen has more actions than belong on the page: the primary action stays a
 * Button, everything else lives here. Destructive items use
 * `variant="destructive"` and go last, after a `DropdownMenuSeparator`.
 */

import * as React from "react"
import { Menu as MenuPrimitive } from "@base-ui/react/menu"

import { cn } from "@/shared/lib/utils"

type MenuSide = "top" | "right" | "bottom" | "left"
type MenuAlign = "start" | "center" | "end"

function DropdownMenu(props: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root {...props} />
}

function DropdownMenuTrigger(props: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
}

function DropdownMenuContent({
  className,
  side = "bottom",
  align = "end",
  sideOffset = 6,
  children,
  ...props
}: MenuPrimitive.Popup.Props & {
  side?: MenuSide
  align?: MenuAlign
  sideOffset?: number
}) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        className="isolate z-50"
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            "min-w-[220px] origin-(--transform-origin) rounded-lg border border-[var(--rule-strong)] bg-[var(--bg-card)] p-1 text-[var(--ink)] shadow-[var(--shadow-float)] outline-none duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function DropdownMenuGroup(props: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

function DropdownMenuLabel({
  className,
  ...props
}: MenuPrimitive.GroupLabel.Props) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      className={cn(
        "px-2.5 pt-2 pb-1 text-[13px] font-medium text-[var(--ink-2)]",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuItem({
  className,
  variant = "default",
  ...props
}: MenuPrimitive.Item.Props & { variant?: "default" | "destructive" }) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-variant={variant}
      className={cn(
        "flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-[var(--ink)] outline-none select-none data-highlighted:bg-[var(--bg-subtle)] data-disabled:cursor-not-allowed data-disabled:opacity-50 [&_svg]:size-3.5 [&_svg]:shrink-0",
        variant === "destructive" &&
          "text-[var(--status-error)] data-highlighted:bg-[var(--status-error-bg-subtle)]",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.Separator>) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("mx-1.5 my-1 h-px bg-[var(--rule)]", className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
}
